import { CHAT_LIMITS, type ChatConversationView, type ChatMessageView } from '@mogzo/shared';
import {
  ArrowLeft,
  Ban,
  Bell,
  BellOff,
  CircleAlert,
  Copy,
  Flag,
  Link2,
  LogOut,
  Maximize2,
  SendHorizontal,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import {
  type FormEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router';
import { ApiError } from '../../lib/api';
import {
  acceptRequest,
  blockPlayer,
  type ChatLiveEvent,
  changeChat,
  clock,
  dayLabel,
  declineRequest,
  deleteMessage,
  fetchConversation,
  hideChat,
  leaveGroup,
  markChatRead,
  refreshChatCounts,
  sendMessage,
  useChatLive,
} from '../../lib/chat';
import { Avatar } from '../community/Avatar';
import { Alert, buttonClass } from '../ui';
import { GroupPanel } from './GroupPanel';
import { ChatAvatar, type MenuItem, PopMenu, Say } from './parts';
import { ReportDialog } from './ReportDialog';

const joined = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

function aboutLine(chat: ChatConversationView) {
  if (chat.kind === 'match') return `${chat.members.length} players in this match`;
  if (chat.kind === 'group') {
    return chat.members.length === 1 ? 'Just you' : `${chat.members.length} people`;
  }
  const played = chat.about?.playedTogether ?? [];
  if (played.length) return `You’ve played ${played.join(', ')} together`;
  return chat.about ? `Joined MOGZO in ${joined(chat.about.joinedAt)}` : '';
}

type Problem = { kind: 'refused' | 'links' | 'other'; text: string };

/**
 * One chat: its messages, newest at the bottom, and the box to write in. Used in the chat window
 * (`dock`) and on Messages (`page`, with a group's people beside it on a computer). It follows
 * the live connection, so new messages, read marks and deletions show at once.
 */
export function ChatView({
  id,
  variant,
  onBack,
  onClose,
  onGone,
}: {
  id: string;
  variant: 'dock' | 'page';
  onBack?: () => void;
  onClose?: () => void;
  /** The chat went away for this player: deleted for them, left or declined. */
  onGone: () => void;
}) {
  const [chat, setChat] = useState<ChatConversationView | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [reporting, setReporting] = useState<ChatMessageView | null>(null);
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [older, setOlder] = useState(false);
  const thread = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const reading = useRef(false);

  const markRead = useCallback(() => {
    if (reading.current || document.visibilityState !== 'visible') return;
    reading.current = true;
    markChatRead(id)
      .then(refreshChatCounts)
      .catch(() => undefined)
      .finally(() => {
        reading.current = false;
      });
  }, [id]);

  const load = useCallback(async () => {
    try {
      const next = await fetchConversation(id);
      setChat(next);
      setFailed(null);
      if (next.status === 'accepted' && next.unread > 0) markRead();
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) onGone();
      else setFailed(error instanceof ApiError ? error.message : 'Could not load this chat.');
    }
  }, [id, markRead, onGone]);

  useEffect(() => {
    setChat(null);
    setText('');
    setProblem(null);
    atBottom.current = true;
    void load();
  }, [load]);

  const onLive = useCallback(
    (event: ChatLiveEvent) => {
      if (event.type === 'message' && event.message.conversationId === id) {
        setChat((was) =>
          was && !was.messages.some((m) => m.id === event.message.id)
            ? { ...was, messages: [...was.messages, event.message] }
            : was,
        );
        if (!event.message.mine) markRead();
      } else if (event.type === 'deleted' && event.conversationId === id) {
        setChat((was) =>
          was
            ? {
                ...was,
                messages: was.messages.map((m) =>
                  m.id === event.messageId ? { ...m, deleted: true, text: '' } : m,
                ),
              }
            : was,
        );
      } else if (
        (event.type === 'read' && event.conversationId === id) ||
        (event.type === 'changed' && (event.conversationId === id || !event.conversationId))
      ) {
        void load();
      }
    },
    [id, load, markRead],
  );
  useChatLive(true, onLive);

  // Stays at the newest message while the player is there.
  // biome-ignore lint/correctness/useExhaustiveDependencies: scrolls whenever the messages change
  useEffect(() => {
    const box = thread.current;
    if (box && atBottom.current && !older) box.scrollTop = box.scrollHeight;
  }, [chat?.messages]);

  async function loadOlder() {
    const first = chat?.messages[0];
    if (!chat || !first) return;
    setOlder(true);
    try {
      const page = await fetchConversation(id, first.createdAt);
      setChat({ ...chat, messages: [...page.messages, ...chat.messages], hasMore: page.hasMore });
    } finally {
      setOlder(false);
    }
  }

  async function send(event?: FormEvent) {
    event?.preventDefault();
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    setProblem(null);
    try {
      const { message } = await sendMessage(id, body);
      setText('');
      atBottom.current = true;
      setChat((was) =>
        was && !was.messages.some((m) => m.id === message.id)
          ? { ...was, messages: [...was.messages, message] }
          : was,
      );
    } catch (error) {
      const code = error instanceof ApiError ? error.code : '';
      const message = error instanceof ApiError ? error.message : 'Could not send it. Try again.';
      setProblem({
        kind: code === 'message_refused' ? 'refused' : code === 'links_not_yet' ? 'links' : 'other',
        text: message,
      });
      if (code === 'chat_off' || code === 'you_blocked') void load();
    } finally {
      setSending(false);
    }
  }

  function onKey(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void send();
    }
  }

  async function act(action: () => Promise<unknown>, gone = false) {
    try {
      await action();
      refreshChatCounts();
      if (gone) onGone();
      else await load();
    } catch (error) {
      setFailed(error instanceof ApiError ? error.message : 'That didn’t work. Try again.');
    }
  }

  if (!chat) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center p-6 text-center text-muted">
        {failed ? <Alert>{failed}</Alert> : <span role="status">Loading the chat…</span>}
      </div>
    );
  }

  const lastTheirs = chat.messages.findLast((m) => !m.mine && !m.deleted);
  const menu: (MenuItem | 'separator')[] = [
    chat.muted
      ? {
          label: 'Unmute this chat',
          icon: Bell,
          onSelect: () => act(() => changeChat(id, { muted: false })),
        }
      : {
          label: 'Mute this chat',
          icon: BellOff,
          onSelect: () => act(() => changeChat(id, { muted: true })),
        },
    ...(chat.kind === 'group'
      ? [{ label: 'People', icon: Users, onSelect: () => setPeopleOpen(true) }]
      : []),
    {
      label: 'Delete this chat for you',
      icon: Trash2,
      onSelect: () => {
        if (window.confirm('Delete this chat for you? The others keep their copy.')) {
          void act(() => hideChat(id), true);
        }
      },
    },
    ...(chat.kind === 'match' ? [] : (['separator'] as const)),
    ...(chat.kind === 'match'
      ? []
      : chat.kind === 'group'
        ? [
            {
              label: 'Leave group',
              icon: LogOut,
              danger: true,
              onSelect: () => {
                if (window.confirm(`Leave ${chat.name}?`)) void act(() => leaveGroup(id), true);
              },
            },
          ]
        : [
            {
              label: `Block ${chat.name}`,
              icon: Ban,
              danger: true,
              onSelect: () => {
                if (window.confirm(`Block ${chat.name}? They won’t be told.`)) {
                  void act(() => blockPlayer(chat.name));
                }
              },
            },
          ]),
  ];
  const showPanel = chat.kind === 'group' && (peopleOpen || variant === 'page');

  return (
    <div className="flex min-h-0 flex-1">
      <section
        aria-label={`Chat with ${chat.name}`}
        className="flex min-h-0 min-w-0 flex-1 flex-col bg-card"
      >
        <header
          className={`flex shrink-0 items-center gap-2 border-b border-line ${variant === 'dock' ? 'min-h-16 pr-2 pl-1.5' : 'min-h-[68px] px-2 lg:pr-4 lg:pl-5'}`}
        >
          {onBack && (
            <button
              type="button"
              aria-label="Back to Messages"
              onClick={onBack}
              className={`flex size-10 shrink-0 items-center justify-center rounded-full hover:bg-field ${variant === 'page' ? 'lg:hidden' : ''}`}
            >
              <ArrowLeft size={20} strokeWidth={1.9} aria-hidden="true" />
            </button>
          )}
          <ChatAvatar chat={chat} size="md" />
          <div className="min-w-0 flex-1 leading-[18px]">
            <h2 className="truncate text-[15px] font-semibold">{chat.name}</h2>
            <p className="truncate text-[13px] text-muted">{aboutLine(chat)}</p>
          </div>
          {variant === 'dock' && (
            <Link
              to={`/messages/${id}`}
              aria-label="Open this chat in Messages"
              className="flex size-10 shrink-0 items-center justify-center rounded-full hover:bg-field"
            >
              <Maximize2 size={18} strokeWidth={1.9} aria-hidden="true" />
            </Link>
          )}
          <PopMenu label="More for this chat" items={menu} />
          {onClose && (
            <button
              type="button"
              aria-label="Close messages"
              onClick={onClose}
              className="flex size-10 shrink-0 items-center justify-center rounded-full hover:bg-field"
            >
              <X size={20} strokeWidth={1.9} aria-hidden="true" />
            </button>
          )}
        </header>

        <div
          ref={thread}
          onScroll={(event) => {
            const box = event.currentTarget;
            atBottom.current = box.scrollHeight - box.scrollTop - box.clientHeight < 60;
          }}
          className={`flex min-h-0 flex-1 flex-col gap-[3px] overflow-y-auto ${variant === 'dock' ? 'px-4 pt-3 pb-4' : 'px-4 pt-4 pb-4 lg:px-6'}`}
        >
          {chat.hasMore ? (
            <button
              type="button"
              onClick={loadOlder}
              disabled={older}
              className="mx-auto my-2 rounded-full border border-line px-4 py-1.5 text-sm font-semibold hover:bg-field"
            >
              {older ? 'Loading…' : 'Show earlier messages'}
            </button>
          ) : (
            <div className="flex flex-col items-center gap-1.5 pt-3.5 pb-5 text-center">
              {chat.kind === 'direct' ? (
                <Avatar name={chat.name} size="xl" />
              ) : (
                <ChatAvatar chat={chat} />
              )}
              <b className="text-[17px] font-bold">{chat.name}</b>
              <span className="max-w-[360px] text-[13px] leading-[18px] text-muted">
                {chat.kind === 'direct'
                  ? [
                      chat.about ? `Joined MOGZO in ${joined(chat.about.joinedAt)}` : null,
                      chat.about?.playedTogether.length
                        ? `You’ve played ${chat.about.playedTogether.join(', ')} together`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : chat.kind === 'match'
                    ? 'Everyone in this match. Kept 7 days after it ends.'
                    : `${chat.members.find((m) => m.owner)?.username ?? 'Someone'} made this group.`}
              </span>
            </div>
          )}
          {chat.messages.map((message, i) => {
            const before = chat.messages[i - 1];
            const newDay = !before || dayLabel(before.createdAt) !== dayLabel(message.createdAt);
            const newRun = newDay || before?.from.id !== message.from.id;
            const isLast = i === chat.messages.length - 1;
            const items: (MenuItem | 'separator')[] = message.deleted
              ? []
              : [
                  {
                    label: 'Copy text',
                    icon: Copy,
                    onSelect: () => void navigator.clipboard?.writeText(message.text),
                  },
                  message.mine
                    ? {
                        label: 'Delete',
                        icon: Trash2,
                        danger: true,
                        onSelect: () => {
                          if (window.confirm('Delete this message for everyone?')) {
                            void act(() => deleteMessage(id, message.id));
                          }
                        },
                      }
                    : {
                        label: 'Report this message',
                        icon: Flag,
                        danger: true,
                        onSelect: () => setReporting(message),
                      },
                ];
            return (
              <div key={message.id} className="contents">
                {newDay && (
                  <p className="my-2.5 self-center text-xs font-semibold text-muted">
                    {dayLabel(message.createdAt)}
                  </p>
                )}
                <div
                  className={`group flex max-w-[78%] items-center gap-1 ${message.mine ? 'flex-row-reverse self-end' : 'self-start'} ${newRun && !newDay ? 'mt-2.5' : ''}`}
                >
                  <div
                    className={`flex min-w-0 flex-col ${message.mine ? 'items-end' : 'items-start'}`}
                  >
                    {newRun && chat.kind !== 'direct' && !message.mine && (
                      <span className="mb-[3px] ml-3.5 text-xs font-semibold text-muted">
                        {message.from.username}
                      </span>
                    )}
                    <p
                      className={`m-0 rounded-[20px] px-3.5 py-2 text-[15px] leading-[21px] break-words whitespace-pre-wrap ${
                        message.deleted
                          ? 'border border-dashed border-line text-muted italic'
                          : message.mine
                            ? 'bg-ink text-white'
                            : 'bg-field'
                      }`}
                    >
                      {message.deleted ? 'Message deleted' : message.text}
                    </p>
                    {(chat.seenUpTo === message.id || isLast) && (
                      <span className="mx-2 mt-[3px] text-xs text-subtle">
                        {chat.seenUpTo === message.id ? 'Seen' : clock(message.createdAt)}
                      </span>
                    )}
                  </div>
                  {items.length > 0 && (
                    <div className="opacity-100 lg:opacity-0 lg:group-focus-within:opacity-100 lg:group-hover:opacity-100">
                      <PopMenu
                        small
                        label="More for this message"
                        items={items}
                        align={message.mine ? 'right' : 'left'}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {chat.status === 'request' ? (
          <div className="flex shrink-0 flex-col gap-3 border-t border-line px-5 py-4">
            <p className="font-semibold">Let {chat.name} message you?</p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => act(() => acceptRequest(id))}
                className={buttonClass('primary', false)}
              >
                Accept
              </button>
              <button
                type="button"
                onClick={() => act(() => declineRequest(id), true)}
                className={buttonClass('secondary', false)}
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() =>
                  act(async () => {
                    await blockPlayer(chat.name);
                    await declineRequest(id);
                  }, true)
                }
                className={`${buttonClass('secondary', false)} text-[#c23a32]`}
              >
                Block
              </button>
              {lastTheirs && (
                <button
                  type="button"
                  onClick={() => setReporting(lastTheirs)}
                  className="ml-auto px-3 py-2 text-sm font-bold text-muted hover:text-ink"
                >
                  Report
                </button>
              )}
            </div>
            <p className="text-[13px] leading-[18px] text-muted">
              Accept moves the chat to Chats. Delete removes it; they can ask again in{' '}
              {CHAT_LIMITS.declinedDays} days. Block stops their messages and requests, and they
              aren’t told.
            </p>
          </div>
        ) : chat.cantSend ? (
          <div className="shrink-0 border-t border-line p-3">
            <Say tone="warning" icon={Ban}>
              {chat.cantSend}
              {chat.cantSend.startsWith('Your chat is off') &&
                ' If you think it’s a mistake, write to hello@mogzo.com.'}
            </Say>
          </div>
        ) : (
          <form onSubmit={send} className="shrink-0 border-t border-line bg-card">
            {problem && (
              <div className="px-3 pt-2.5">
                {problem.kind === 'refused' ? (
                  <Say tone="danger" icon={CircleAlert}>
                    <b className="font-semibold">Not sent.</b> {problem.text}
                  </Say>
                ) : problem.kind === 'links' ? (
                  <Say tone="info" icon={Link2}>
                    {problem.text}
                  </Say>
                ) : (
                  <Say tone="danger" icon={CircleAlert}>
                    {problem.text}
                  </Say>
                )}
              </div>
            )}
            <div className="flex items-end gap-2 px-3 pt-2.5 pb-3">
              <label htmlFor={`box-${id}`} className="sr-only">
                Message {chat.name}
              </label>
              <textarea
                id={`box-${id}`}
                value={text}
                rows={1}
                maxLength={CHAT_LIMITS.messageLength}
                placeholder={`Message ${chat.name}`}
                onChange={(event) => {
                  setText(event.target.value);
                  if (problem) setProblem(null);
                }}
                onKeyDown={onKey}
                className={`field-sizing-content max-h-36 min-h-11 flex-1 resize-none rounded-[22px] border px-4 py-2.5 text-[15px] leading-[21px] focus:bg-card focus:outline-none ${problem?.kind === 'refused' ? 'border-[#c23a32] bg-card' : 'border-transparent bg-field focus:border-ink'}`}
              />
              <button
                type="submit"
                aria-label="Send"
                disabled={!text.trim() || sending}
                className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand text-ink disabled:bg-field disabled:text-subtle"
              >
                <SendHorizontal size={20} strokeWidth={1.9} aria-hidden="true" />
              </button>
            </div>
          </form>
        )}
      </section>

      {showPanel && (
        <GroupPanel
          chat={chat}
          floating={variant === 'dock' || peopleOpen}
          onClose={() => setPeopleOpen(false)}
          onChanged={load}
          onGone={onGone}
        />
      )}
      {reporting && (
        <ReportDialog
          message={reporting}
          onClose={(blocked) => {
            setReporting(null);
            if (blocked) void load();
          }}
        />
      )}
    </div>
  );
}
